import React, { createContext, useContext, ReactNode } from 'react';
import { UseDashboardDataResult } from '../hooks/useDashboardData';

/**
 * DashboardContext provides dashboard data to deeply nested components
 * without prop drilling.
 * 
 * This context is populated by PortfolioLayout with data from useDashboardData,
 * and consumed by child components like PortfolioPage, charts, tables, etc.
 */

interface DashboardContextValue extends UseDashboardDataResult {}

const DashboardContext = createContext<DashboardContextValue | null>(null);

interface DashboardProviderProps {
  children: ReactNode;
  value: DashboardContextValue;
}

/**
 * Provider component that makes dashboard data available to all children
 */
export const DashboardProvider: React.FC<DashboardProviderProps> = ({ 
  children, 
  value 
}) => {
  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
};

/**
 * Hook to access dashboard data from context
 * 
 * Must be used within a DashboardProvider
 */
export const useDashboard = (): DashboardContextValue => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

